import { useCallback } from 'react';
import { Text, Divider, Blockquote } from '@mantine/core';
import {
  EditableText,
  type EditableTextSuggestOption,
} from '@ayocore/platform/client/components/EditableText';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import { useNavigate } from 'react-router';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { useUserRepository } from '@ayocore/platform/identity/client/rpc';
import { PartnerView } from '../../../Partners/views';
import { DealPartnerField } from './DealPartnerField';
import type { DealExtended } from '../DealViewController';

interface DealInfoProps {
  deal: DealExtended;
  canEdit?: boolean;
  onPartnerClick?: (partnerId: number) => void;
  onPartnerSave?: (partnerId: number | null) => void | Promise<void>;
  onOwnerSave?: (ownerId: number | null) => void | Promise<void>;
  onNotesSave?: (notes: string) => void | Promise<void>;
  onDescriptionSave?: (description: string) => void | Promise<void>;
}

const placeholderClass = 'entity-view-placeholder-dimmed';

/**
 * Блок общей информации о сделке: партнёр, ответственный, описание, примечания.
 * При canEdit — EditableText с саджестом для выбора из базы (кроме партнёра —
 * см. DealPartnerField: привязанный партнёр — только ссылка+открепление,
 * редактирование данных партнёра — через его собственную карточку).
 */
export function DealInfo({
  deal,
  canEdit = false,
  onPartnerClick,
  onPartnerSave,
  onOwnerSave,
  onNotesSave,
  onDescriptionSave,
}: DealInfoProps) {
  const navigate = useNavigate();
  const partnerDrawer = useDrawer(PartnerView, {
    openInTab: props => navigate(`/crm/partners/${props.partnerId}`),
  });
  const userRepository = useUserRepository();

  const ownerValue = deal.deal_owner
    ? deal.deal_owner.name || deal.deal_owner.email || `ID: ${deal.deal_owner.id}`
    : '';

  const searchUsers = useCallback(
    async (query: string): Promise<EditableTextSuggestOption[]> => {
      const rows = await userRepository.search(query).orderBy('name', 'asc').limit(15).fetch();
      return (rows ?? []).map(u => ({
        id: String(u.id),
        label: u.name ?? u.email ?? `ID: ${u.id}`,
        hint: u.email ?? undefined,
        value: u.name ?? u.email ?? '',
      }));
    },
    [userRepository]
  );

  const ownerEditable = canEdit && !!onOwnerSave;
  const descriptionEditable = canEdit && !!onDescriptionSave;

  const handlePartnerClick = (partnerId: number) => {
    if (onPartnerClick) {
      onPartnerClick(partnerId);
      return;
    }
    partnerDrawer.open({ partnerId, isModal: true }, {});
  };

  return (
    <ViewPanel>
      <InfoRow>
        <InfoRow.TitleCol>Партнер</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <DealPartnerField
            partner={deal.partner}
            onChange={id => void onPartnerSave?.(id)}
            canEdit={canEdit && !!onPartnerSave}
            onPartnerClick={handlePartnerClick}
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Ответственный</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          {ownerEditable ? (
            <EditableText
              value={ownerValue}
              size='xs'
              canEdit
              placeholder='Добавить ответственного...'
              className={!ownerValue ? placeholderClass : undefined}
              allowEmptySave
              suggest={{
                search: searchUsers,
                minChars: 2,
                debounceMs: 250,
              }}
              onSave={(value, option) => {
                const id = option ? Number(option.id) : null;
                if (option || !value.trim()) void onOwnerSave?.(id);
              }}
              style={{
                minWidth: 0,
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          ) : (
            <Text size='xs' style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              {ownerValue || 'Не назначен'}
            </Text>
          )}
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Описание</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          {descriptionEditable ? (
            <EditableText
              value={deal.description ?? ''}
              size='xs'
              canEdit
              placeholder='Добавить описание...'
              className={!deal.description ? placeholderClass : undefined}
              allowEmptySave
              maxRows={4}
              onSave={v => void onDescriptionSave?.(v)}
              style={{
                minWidth: 0,
                flex: 1,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            />
          ) : (
            <Text
              size='xs'
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {deal.description || 'Не указано'}
            </Text>
          )}
        </InfoRow.ValueCol>
      </InfoRow>

      <Divider my='md' />
      <Blockquote color='gray' radius='xs' p='xs' style={{ fontSize: 12 }}>
        <EditableText
          value={deal.notes ?? ''}
          canEdit={canEdit && !!onNotesSave}
          onSave={onNotesSave ?? (() => {})}
          placeholder='Добавить примечание...'
          size='sm'
          maxRows={6}
          style={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            display: 'block',
            verticalAlign: 'top',
            fontSize: 12,
          }}
        />
      </Blockquote>
    </ViewPanel>
  );
}
